import { useApi } from '@shared/api'
import type {
  Product,
  ProductCreateDto,
  ProductGroupEntity,
  GetAllProductsResponse,
  ProductDetailResponse,
  UpdateProductTypeDto,
  UpdateProductTypeResponse,
  UnassignedProductsCountResponse,
} from '@entities/product'

const BASE = '/admin/products'

/** Ручной выбор пары модификатора, когда одноимённых в филиале несколько */
export interface ProductCopyModifierOverride {
  source_id: number
  target_id: number
}

export interface ProductCopyRequest {
  branch_ids: number[]
  external_product_id?: string
  name?: string
  copy_modifiers?: boolean
  copy_tags?: boolean
  modifier_overrides?: ProductCopyModifierOverride[]
  detach_source_branches?: boolean
  dry_run?: boolean
}

export interface ProductCopyModifierMatch {
  source: {
    id: string
    name: string
    size: string | null
    type: 'addon' | 'exclude'
    required: boolean
    maxQuantity: number
  }
  status: 'matched' | 'ambiguous' | 'missing'
  target: { id: string, name: string, size: string | null } | null
  candidates: Array<{ id: string, name: string, size: string | null }>
  manual: boolean
}

export interface ProductCopyResponse {
  success: boolean
  dry_run: boolean
  id: string | null
  name: string | null
  is_active: boolean
  external_product_id: string | null
  branch_ids: string[]
  conflicting_source_branch_ids: string[]
  source_branches_detached: boolean
  modifiers: ProductCopyModifierMatch[]
  matched_count: number
  ambiguous_count: number
  missing_count: number
}

export const apiAdminProduct = {
  async getAll(params: {
    page?: number
    limit?: number
    category_id?: string
    /** Фильтр по группе товаров (админка: карточка группы) */
    group_id?: number
    search?: string
    is_addon?: boolean
    is_exclude_ingredient?: boolean
    is_product?: boolean
    unassigned?: boolean
    /** Только карточки, доступные в этом филиале */
    branch_id?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  } = {}): Promise<GetAllProductsResponse> {
    const query = new URLSearchParams()
    if (params.page) query.append('page', String(params.page))
    if (params.limit) query.append('limit', String(params.limit))
    if (params.category_id) query.append('category_id', params.category_id)
    if (params.group_id != null) query.append('group_id', String(params.group_id))
    if (params.search) query.append('search', params.search)
    if (params.is_addon !== undefined) query.append('is_addon', String(params.is_addon))
    if (params.is_exclude_ingredient !== undefined) query.append('is_exclude_ingredient', String(params.is_exclude_ingredient))
    if (params.is_product !== undefined) query.append('is_product', String(params.is_product))
    if (params.unassigned !== undefined) query.append('unassigned', String(params.unassigned))
    if (params.branch_id != null) query.append('branch_id', String(params.branch_id))
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)

    const { data } = await useApi(true, true).get<GetAllProductsResponse>(`/admin/products?${query.toString()}`)
    return data
  },

  async getAllAddons(limit = 1000): Promise<Product[]> {
    const response = await apiAdminProduct.getAll({ is_addon: true, limit })
    return response.products as any[]
  },

  async getAllExcludeIngredients(limit = 1000): Promise<Product[]> {
    const response = await apiAdminProduct.getAll({ is_exclude_ingredient: true, limit })
    return response.products as any[]
  },

  async getUnassignedCount(): Promise<number> {
    const { data } = await useApi(true, true).get<UnassignedProductsCountResponse>('/admin/products/unassigned/count')
    return data.count
  },

  async getById(id: number): Promise<ProductDetailResponse> {
    const { data } = await useApi(true, true).get<ProductDetailResponse>(`${BASE}/${id}`)
    return data
  },

  async create(payload: FormData): Promise<Product> {
    const { data } = await useApi(true, true).post<Product>('/admin/products/create_product', { body: payload })
    return data
  },

  async update(id: number, payload: Partial<ProductCreateDto> | FormData): Promise<Product> {
    const { data } = await useApi(true, true).patch<Product>(`${BASE}/${id}`, { body: payload })
    return data
  },

  /**
   * Копия карточки в другой филиал с подбором модификаторов по названию.
   *
   * С `dry_run: true` ничего не создаётся — возвращается только отчёт сопоставления
   * для предпросмотра: неоднозначные и ненайденные модификаторы бэк молча не привязывает.
   */
  async copy(id: number, payload: ProductCopyRequest): Promise<ProductCopyResponse> {
    const { data } = await useApi(true, true).post<ProductCopyResponse>(`${BASE}/${id}/copy`, { body: payload })
    return data
  },

  async delete(id: number): Promise<{ success: boolean; message: string }> {
    const { data } = await useApi(true, true).delete<{ success: boolean; message: string }>(`${BASE}/${id}`)
    return data
  },

  /** @deprecated Use update() with FormData */
  async uploadImg(id: number, formData: FormData): Promise<{ imageKey: string; imageUrl: string }> {
    const { data } = await useApi(true, true).post<{ imageKey: string; imageUrl: string }>(`${BASE}/${id}/img`, { body: formData })
    return data
  },

  async deleteImage(id: number, imageKey: string): Promise<{ message: string; remainingImages: number }> {
    const { data } = await useApi(true, true).delete<{ message: string; remainingImages: number }>(
      `${BASE}/${id}/img/${encodeURIComponent(imageKey)}`,
    )
    return data
  },

  async uploadBackground(id: number, formData: FormData): Promise<{ id: number; name: string; background_img: { key: string; url: string } | null }> {
    const { data } = await useApi(true, true).post<any>(`${BASE}/${id}/background`, { body: formData })
    return data
  },

  async deleteBackground(id: number): Promise<{ message: string }> {
    const { data } = await useApi(true, true).delete<{ message: string }>(`${BASE}/${id}/background`)
    return data
  },

  async updateProductType(institutionId: number, productId: number, payload: UpdateProductTypeDto): Promise<UpdateProductTypeResponse> {
    const { data } = await useApi(true, true).patch<UpdateProductTypeResponse>(
      `/frontpad/products/${institutionId}/${productId}/type`,
      { body: payload },
    )
    return data
  },

  // --- Product Groups ---

  async getProductGroups(params: {
    page?: number
    limit?: number
    search?: string
    is_active?: boolean | null
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  } = {}): Promise<{ groups: ProductGroupEntity[]; pagination: any }> {
    const query = new URLSearchParams()
    if (params.page) query.append('page', String(params.page))
    if (params.limit) query.append('limit', String(params.limit))
    if (params.search) query.append('search', params.search)
    if (params.is_active !== undefined && params.is_active !== null) query.append('is_active', String(params.is_active))
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)

    const { data } = await useApi(true, true).get<{ groups: ProductGroupEntity[]; pagination: any }>(`/admin/product-groups?${query.toString()}`)
    return data
  },

  async getProductGroupById(id: number): Promise<ProductGroupEntity> {
    const { data } = await useApi(true, true).get<ProductGroupEntity>(`/admin/product-groups/${id}`)
    return data
  },

  async createProductGroup(payload: any): Promise<ProductGroupEntity> {
    const { data } = await useApi(true, true).post<ProductGroupEntity>('/admin/product-groups', { body: payload })
    return data
  },

  async updateProductGroup(id: number, payload: any): Promise<ProductGroupEntity> {
    const { data } = await useApi(true, true).patch<ProductGroupEntity>(`/admin/product-groups/${id}`, { body: payload })
    return data
  },

  async deleteProductGroup(id: number): Promise<void> {
    await useApi(true, true).delete(`/admin/product-groups/${id}`)
  },

  async deleteProductGroupImage(id: number, imageKey: string): Promise<{ message: string; remainingImages: number }> {
    const { data } = await useApi(true, true).delete<{ message: string; remainingImages: number }>(
      `/admin/product-groups/${id}/img/${encodeURIComponent(imageKey)}`,
    )
    return data
  },

  async uploadProductGroupBackground(id: number, formData: FormData): Promise<{ id: number; name: string; background_img: { key: string; url: string } | null }> {
    const { data } = await useApi(true, true).post<any>(`/admin/product-groups/${id}/background`, { body: formData })
    return data
  },

  async deleteProductGroupBackground(id: number): Promise<{ message: string }> {
    const { data } = await useApi(true, true).delete<{ message: string }>(`/admin/product-groups/${id}/background`)
    return data
  },

  // --- Combos ---

  async getCombos(params: {
    page?: number
    limit?: number
    search?: string
    is_active?: boolean | null
    category_id?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  } = {}): Promise<{ combos: any[]; pagination: any }> {
    const query = new URLSearchParams()
    if (params.page) query.append('page', String(params.page))
    if (params.limit) query.append('limit', String(params.limit))
    if (params.search) query.append('search', params.search)
    if (params.is_active !== undefined && params.is_active !== null) query.append('is_active', String(params.is_active))
    if (params.category_id != null) query.append('category_id', String(params.category_id))
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)

    const { data } = await useApi(true, true).get<{ combos: any[]; pagination: any }>(`/admin/combos?${query.toString()}`)
    return data
  },

  async getComboById(id: number): Promise<any> {
    const { data } = await useApi(true, true).get<any>(`/admin/combos/${id}`)
    return data
  },

  async createCombo(payload: any): Promise<any> {
    const { data } = await useApi(true, true).post<any>('/admin/combos', { body: payload })
    return data
  },

  async updateCombo(id: number, payload: any): Promise<any> {
    const { data } = await useApi(true, true).patch<any>(`/admin/combos/${id}`, { body: payload })
    return data
  },

  async deleteCombo(id: number): Promise<void> {
    await useApi(true, true).delete(`/admin/combos/${id}`)
  },

  async uploadComboImages(comboId: number, formData: FormData): Promise<any> {
    const { data } = await useApi(true, true).post<any>(`/admin/combos/${comboId}/img`, { body: formData })
    return data
  },

  async deleteComboImage(comboId: number, imageKey: string): Promise<{ success: boolean; message: string }> {
    const { data } = await useApi(true, true).delete<{ success: boolean; message: string }>(
      `/admin/combos/${comboId}/img/${encodeURIComponent(imageKey)}`,
    )
    return data
  },

  async updateComboMediaOrder(comboId: number, mediaOrder: string[]): Promise<any> {
    const { data } = await useApi(true, true).patch<any>(`/admin/combos/${comboId}/media-order`, {
      body: { media_order: mediaOrder },
    })
    return data
  },
}

export default apiAdminProduct
